import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import '../ConfirmationModal/ConfirmationModal.css';
import './MessageDialog.css';

interface MessageDialogProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  message: string;
  confirmText?: string;
  variant?: 'info' | 'error';
}

const MessageDialog: React.FC<MessageDialogProps> = ({
  isOpen,
  onClose,
  title = 'Notice',
  message,
  confirmText = 'OK',
  variant = 'info',
}) => {
  useEffect(() => {
    if (!isOpen) return undefined;
    document.body.style.overflow = 'hidden';
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.body.style.overflow = '';
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return createPortal(
    <div className="confirmation-modal-overlay" onClick={onClose} role="presentation">
      <div
        className="confirmation-modal message-dialog"
        onClick={(e) => e.stopPropagation()}
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="message-dialog-title"
        aria-describedby="message-dialog-body"
      >
        <button type="button" className="confirmation-modal-close" onClick={onClose} aria-label="Close">
          <i className="fa fa-times" />
        </button>
        <div className="confirmation-modal-header">
          <div className={`confirmation-modal-icon message-dialog-icon message-dialog-icon--${variant}`}>
            <i className={`fa ${variant === 'error' ? 'fa-exclamation-circle' : 'fa-info-circle'}`} />
          </div>
          <h2 id="message-dialog-title" className="confirmation-modal-title">
            {title}
          </h2>
        </div>
        <div className="confirmation-modal-body">
          <p id="message-dialog-body" className="confirmation-modal-message">
            {message}
          </p>
        </div>
        <div className="confirmation-modal-footer message-dialog-footer">
          <button
            type="button"
            className="confirmation-modal-btn confirmation-modal-btn-confirm confirmation-modal-btn-primary"
            onClick={onClose}
          >
            {confirmText}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default MessageDialog;
